<template>
  <tabs />
  <toolbar class="toolbar">
    <filter-pane v-model:page="page" v-model="filter" :filters="filters" />
    <add-fields-pane v-model="visibleColumns" :fields="columns" />
    <export
      :url="`/drivers/${props.modelValue?.id}/focus_points/csv`"
      :filter="totalFilter"
      :columns="columns"
      :visible-columns="visibleColumns"
      :title="csvTitle"
      :sort-key="sortKey"
      :sort-direction="sortDirection"
    />
  </toolbar>

  <div class="ml-12">
    <div class="table">
      <data-table
        class="mb-auto"
        v-model:sort-by="sortKey"
        v-model:sort-direction="sortDirection"
        :busy="loading"
        :items="records"
        :columns="columns"
        :row-link="
          (item) => ({
            name: 'campaign-group-focus-point-details',
            params: {
              campaignGroupId: item.focusPoint?.campaign?.group?.id ?? '-',
              focusPointId: item.focusPoint?.id ?? '-'
             },
          })
        "
        :visible-columns="visibleColumns"
      />
    </div>
  </div>
  <pagination v-model:page="page" v-model:per-page="perPage" class="pagination" :total="total" />
</template>

<script lang="tsx" setup>
import { ref, reactive, computed, unref } from 'vue';
import { useI18n } from 'vue-i18n';
import FilterPane from '@utils/toolbar/panes/Filter.vue';
import AddFieldsPane from '@utils/toolbar/panes/AddFields.vue';
import Export from '@components/utils/toolbar/panes/Export.vue';
import Toolbar from '@utils/toolbar/Toolbar.vue';
import Pagination from '@utils/table/Pagination.vue';
import DataTable, { type Column } from '@utils/table/Table.vue';
import {collOptionsProvider, enumOptionsProvider, ProviderParams, useProvider} from '@utils/table/hooks/provider';
import { format } from 'date-fns';
import type { Filter } from '@utils/filter/Filter.vue';
import Tabs from '@components/structure/navigation/Tabs.vue';
import type { Driver, HydraCollection, Payout } from '@/entities/Entities';
import { useQueryRef } from '@/hooks/url';
import {Campaign, Reward} from '@/entities/Entities';
import {formatTimeDurationFromSeconds} from '@/utils/helpers';
import {useTableFields} from '@hooks/table-configuration';

interface Props {
  modelValue: Driver | null;
}

const props = defineProps<Props>();

const page = useQueryRef('page', 1);
const perPage = useQueryRef('perPage', 10);
const sortKey = useQueryRef<string | undefined>('sortKey', undefined);
const sortDirection = useQueryRef<'ASC' | 'DESC' | undefined>('sortDirection', undefined);

const { t } = useI18n();

const csvTitle = computed(() => {
  return t('csv.title.focusPointsFromDriver', {
    driver: props.modelValue?.user?.fullName,
  });
});

const filter = useQueryRef('filter', {});
const totalFilter = computed(() => {
  return { ...filter.value };
});

const providerParams = reactive({
  sort: {
    sortKey,
    sortDirection,
  },
  filter: totalFilter,
  pagination: {
    page,
    perPage,
  },
});

type ColumnFilter = Partial<Filter> & Omit<Filter, 'key'>;
type ColumnDefinition = Column & { filter?: ColumnFilter | ColumnFilter[] } & { group: string };

const columns = computed<ColumnDefinition[]>(() => [
  {
    key: 'id',
    title: t('id'),
    group: t('pages.driver.focusPointList.fieldGroups.general'),
    computed: (item) => item.focusPoint?.id,
    class: 'whitespace-nowrap',
    visible: false,
    sortable: false,
  },
  {
    key: 'name',
    title: t('focusPoint.name'),
    group: t('pages.driver.focusPointList.fieldGroups.general'),
    computed: (item) => item.focusPoint?.name,
    sortable: true,
    visible: true,
    filter: {
      type: 'text',
      group: t('pages.driver.focusPointList.filterGroups.general'),
      options: {
        label: t('focusPoint.name'),
      },
    },
  },
  {
    key: 'campaign.name',
    title: t('focusPoint.campaign'),
    group: t('pages.driver.focusPointList.fieldGroups.general'),
    computed: (item) => item.focusPoint?.campaign?.name,
    visible: true,
    filter: {
      key: 'campaign',
      type: 'dropdown',
      group: t('pages.driver.focusPointList.filterGroups.general'),
      options: {
        searchable: true,
        minChars: 2,
        filterResults: false,
        clearOnSelect: true,
        delay: 500,
        createOption: false,
        resolveOnLoad: true,
        noOptionsText: t('inputSearchString'),
        options: (query: string, select: any) => {
          const input = query || select.modelValue;
          if (!input) return Promise.resolve([]);
          const filterQuery: ProviderParams = {
            filter: query ? { 'campaign.group.name': input, driver: props.modelValue?.id ?? '' } : { 'campaign': select.modelValue, driver: props.modelValue?.id ?? '' },
          };
          return collOptionsProvider<Campaign>('/campaign_registrations', filterQuery, (value) => ({
            label: value.campaign.name,
            value: value.campaign.id,
          }))();
        },
        label: t('focusPoint.campaign'),
      },
    },
  },
  {
    key: 'numberOfPhotos',
    sortKey: `${props.modelValue?.id}][numberOfPhotos`,
    title: t('focusPoint.totalPhotos'),
    group: t('pages.driver.focusPointList.fieldGroups.general'),
    visible: true,
    sortable: true,
  },
  {
    key: 'standingTime',
    sortKey: `${props.modelValue?.id}][standingTime`,
    title: t('focusPoint.totalTime'),
    group: t('pages.driver.focusPointList.fieldGroups.general'),
    formatter: (v) => (v ? formatTimeDurationFromSeconds(v) : '00:00:00'),
    visible: true,
    sortable: true,
  },
  {
    key: 'numberOfStops',
    sortKey: `${props.modelValue?.id}][numberOfStops`,
    title: t('focusPoint.totalStops'),
    group: t('pages.driver.focusPointList.fieldGroups.general'),
    visible: true,
    sortable: true,
  },
]);

const visibleColumns = useTableFields(
    columns,
);

const blockRequest = computed(() => !props.modelValue?.id);

const {
  data: items,
  loading,
  refetch,
} = useProvider<HydraCollection<Payout>>(computed(() =>`/drivers/${props.modelValue?.id}/focus_points`), providerParams, {
  disabled: blockRequest,
});

const records = computed(() => unref(items)?.['hydra:member']);
const total = computed(() => unref(items)?.['hydra:totalItems']);
const filters = computed(() =>
    columns.value.flatMap((col): Filter[] => {
    if (!col.filter) return [];
    if (Array.isArray(col.filter))
      return col.filter.map((f) => ({
        key: col.key,
        ...f,
      }));
    return [{ key: col.key, ...col.filter }];
  })
);
</script>

<style lang="scss" scoped>
button {
  @apply block;
}

.table {
  @apply flex flex-col;
}
</style>
